import React from "react";
import { Search, BarChart3, Target, Settings, Activity } from "lucide-react";

const ProcessSection = () => {
    const steps = [
        {
            number: "1",
            title: "Diagnóstico Financiero Personalizado",
            desc: "Analizamos tu situación actual, tus metas y tus preocupaciones. Ya seas una persona que quiere ahorrar o una empresa que necesita financiamiento, comenzamos entendiendo tu contexto.",
            icon: Search,
            color: "blue"
        },
        {
            number: "2",
            title: "Análisis y Comparación de Opciones",
            desc: "Evaluamos diferentes soluciones con base en tu perfil financiero. Comparamos entre múltiples productos, instituciones y escenarios para identificar lo más conveniente y seguro para ti.",
            icon: BarChart3,
            color: "green"
        },
        {
            number: "3",
            title: "Asesoría en la Toma de Decisiones",
            desc: "Te explicamos con claridad los beneficios, riesgos y términos de cada opción. No tomamos decisiones por ti, pero sí te damos las herramientas y la información que necesitas para elegir con seguridad.",
            icon: Target,
            color: "purple"
        },
        {
            number: "4",
            title: "Gestión del Proceso y Trámites",
            desc: "Nos encargamos de los procesos operativos, seguimiento con instituciones financieras y resolución de dudas técnicas. Tú te concentras en tus objetivos; nosotros en que llegues a ellos.",
            icon: Settings,
            color: "orange"
        },
        {
            number: "5",
            title: "Evaluación Continua y Ajustes",
            desc: "Revisamos contigo cada avance, cada cambio en tus metas o entorno. Ajustamos la estrategia asegurando que tu plan no solo siga vigente, sino que evolucione contigo y con tu visión.",
            icon: Activity,
            color: "red"
        }
    ];

    const colorMap = {
        blue: "bg-blue-100 border-blue-300",
        green: "bg-green-100 border-green-300",
        purple: "bg-purple-100 border-purple-300",
        orange: "bg-orange-100 border-orange-300",
        red: "bg-red-100 border-red-300"
    };

    const iconColorMap = {
        blue: "bg-blue-200 text-blue-700",
        green: "bg-green-200 text-green-700",
        purple: "bg-purple-200 text-purple-700",
        orange: "bg-orange-200 text-orange-700",
        red: "bg-red-200 text-red-700"
    };

    return (
        <section className="py-12 md:py-16 bg-gradient-to-b from-slate-50 to-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-8 md:mb-12">
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                        Nuestro Proceso de Asesoría
                    </h2>
                    <p className="text-base sm:text-lg md:text-xl text-slate-600 max-w-3xl mx-auto">
                        En MiguelMX, creemos que una asesoría financiera efectiva comienza con un diagnóstico profundo y personalizado.
                    </p>
                </div>

                {/* Desktop Timeline */}
                <div className="hidden lg:block relative">
                    {/* Timeline Line */}
                    <div className="absolute top-16 left-0 w-full h-1 bg-gradient-to-r from-blue-300 via-green-300 via-purple-300 via-orange-300 to-red-300"></div>

                    <div className="grid grid-cols-5 gap-4">
                        {steps.map((step, index) => (
                            <div key={index} className="relative">
                                {/* Timeline Node */}
                                <div className="absolute top-[3.75rem] left-1/2 -translate-x-1/2 w-6 h-6 bg-[#14840c] rounded-full border-4 border-white shadow"></div>

                                {/* Content Card */}
                                <div className={`${colorMap[step.color]} border p-4 md:p-6 rounded-xl mt-20 shadow-md`}>
                                    {/* Step Number */}
                                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-10 h-10 bg-[#14840c] text-white rounded-full flex items-center justify-center font-bold text-lg shadow">
                                        {step.number}
                                    </div>

                                    {/* Icon */}
                                    <div className={`w-12 h-12 ${iconColorMap[step.color]} rounded-lg flex items-center justify-center mb-3 mx-auto`}>
                                        <step.icon className="w-6 h-6" />
                                    </div>

                                    {/* Content */}
                                    <h3 className="text-base md:text-lg font-semibold text-slate-900 mb-2 text-center">
                                        {step.title}
                                    </h3>
                                    <p className="text-sm md:text-base text-slate-600 leading-relaxed text-center">
                                        {step.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Mobile/Tablet Timeline */}
                <div className="lg:hidden relative">
                    {/* Vertical Timeline Line */}
                    <div className="absolute left-6 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-300 via-green-300 via-purple-300 via-orange-300 to-red-300"></div>

                    <div className="space-y-8">
                        {steps.map((step, index) => (
                            <div key={index} className="relative">
                                {/* Timeline Node */}
                                <div className="absolute left-[1.125rem] top-4 w-4 h-4 bg-[#14840c] rounded-full border-3 border-white shadow"></div>

                                {/* Content Card */}
                                <div className={`ml-12 ${colorMap[step.color]} border p-4 rounded-xl shadow-md`}>
                                    {/* Step Number */}
                                    <div className="absolute -left-14 top-2 w-8 h-8 bg-[#14840c] text-white rounded-full flex items-center justify-center font-bold text-base shadow">
                                        {step.number}
                                    </div>

                                    <div className="flex items-start gap-3">
                                        {/* Icon */}
                                        <div className={`w-10 h-10 ${iconColorMap[step.color]} rounded-lg flex items-center justify-center flex-shrink-0`}>
                                            <step.icon className="w-5 h-5" />
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1">
                                            <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-2">
                                                {step.title}
                                            </h3>
                                            <p className="text-sm text-slate-600 leading-relaxed">
                                                {step.desc}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Call to Action */}
                <div className="text-center mt-8 md:mt-12">
                    <div className="bg-white border border-slate-200 p-6 rounded-xl shadow-md max-w-xl mx-auto">
                        <h3 className="text-xl sm:text-2xl md:text-3xl font-semibold text-slate-900 mb-3">
                            ¿Listo para comenzar tu transformación financiera?
                        </h3>
                        <p className="text-sm sm:text-base text-slate-600 mb-4">
                            Nuestro equipo está preparado para acompañarte en cada paso del proceso.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <button className="bg-[#14840c] text-white px-6 py-2 rounded-lg font-medium shadow">
                                Iniciar Diagnóstico Gratuito
                            </button>
                            <button className="border-2 border-[#14840c] text-[#14840c] px-6 py-2 rounded-lg font-medium">
                                Agendar Consulta
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ProcessSection;